<template>
  <input type="text"
    ref="inputRef"
    :value="modelValue"
    readonly
    class="form-control in-place"
    :class="{ 'visible-focus': showFocusRef }"
    aria-describedby="edit-instructions"
    @blur="onBlur"
    @click="onClick"
    @keydown.tab="showFocusRef = true"
    @mousedown="showFocusRef = false"
    @keydown.enter.prevent="onEnter"
    @keydown.space="onSpace"
    @keydown.escape="endEditing(false)"
  >
</template>

<style scoped lang="scss">
.in-place {
  &[readonly] {
    cursor: pointer;
    border: var(--bs-border-width) solid transparent;
    padding: 0.25rem 0;
  }

  &[readonly].visible-focus:focus {
    box-shadow: 0 0 3px rgba(0, 123, 255, 0.5);
    outline: none;
  }

  &:disabled {
    background-color: inherit;
    cursor: default;
  }

  &.rejected {
    animation: shake 0.2s cubic-bezier(.17,.67,.83,.67) both;
    transform-origin: center;
  }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-8px); }
  40% { transform: translateX(8px); }
  60% { transform: translateX(-5px); }
  80% { transform: translateX(5px); }
}
</style>

<script lang="ts" setup>
import { ref } from 'vue'
import { AsyncUtil } from './AsyncUtil'

const props = defineProps<{
  modelValue?: string
  required?: boolean
}>()

const emit = defineEmits([
  'update:modelValue',
])

defineExpose({
  edit,
  save,
  cancel,
})

const showFocusRef = ref(false)
const isEditing = ref(false)
const originalValue = ref('')
const inputRef = ref<HTMLInputElement>()

function onBlur() {
  endEditing(true)
}

function onClick() {
  if (!isEditing.value) {
    edit()
  }
}

function onEnter() {
  if (isEditing.value) {
    save()
  }
  else {
    edit()
  }
}

function onSpace() { edit() }

function edit() {
  if (inputRef.value && !isEditing.value && !inputRef.value.disabled) {
    isEditing.value = true
    originalValue.value = inputRef.value.value
    inputRef.value.removeAttribute('readonly')
  }
}

function save() { endEditing(true) }
function cancel() { endEditing(false)}

async function endEditing(save: boolean) {
  if (inputRef.value && isEditing.value) {
    if (save && props.required && !inputRef.value.value) {
      inputRef.value.classList.add('rejected')
      await AsyncUtil.delay(500)
      inputRef.value.classList.remove('rejected')
      return
    }

    isEditing.value = false
    inputRef.value.setAttribute('readonly', 'true')
    showFocusRef.value = false
    if (save) {
      emit('update:modelValue', inputRef.value.value)
    }
    else {
      inputRef.value.value = originalValue.value
    }
   
  }
}
</script>
